/** @jsx jsx */
import React, { useContext, useMemo, memo } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import ViewportContext from '@atlassian/jira-software-deployments-common-table/src/components/table/common/context/viewport/context';
import type { PercentPosition } from '@atlassian/jira-software-deployments-common-table/src/components/table/common/utils/timeline';
import { DEFAULT_ITEM_HEIGHT } from '@atlassian/jira-software-deployments-common-table/src/model/table';
import { timelineScroller } from '@atlassian/jira-software-deployments-common-table/src/utils';
import type { Deployment } from '../../../../common/types';
import {
	getMostMeaningfulDeployment,
	getGroupedDeployments,
	areDeploymentsEqual,
} from '../../../../common/utils/deployments';
import { useNavigateIndicatorInfo, Directions } from '../../../../controllers/deployments';
import {
	getPastIndicatorInfo,
	getFutureIndicatorInfo,
} from '../../../../controllers/deployments/utils';
import { useTimelineMode } from '../../../../controllers/timeline-mode';
import NavigateIndicator from './navigate-indicator';

type Props = {
	getPositionFromDate: (date: Date) => PercentPosition;
	deployments: Deployment[];
};

const NavigateIndicators = ({ deployments: deploymentsSorted, getPositionFromDate }: Props) => {
	const [timelineMode] = useTimelineMode();
	const deploymentGroups = getGroupedDeployments(
		deploymentsSorted,
		getPositionFromDate,
		timelineMode,
	);
	const singleDeployments = deploymentGroups.map((group) => group.deployments[0]);
	const direction = useNavigateIndicatorInfo(singleDeployments, getPositionFromDate);
	const { requestViewportScrollBounding } = useContext(ViewportContext);

	const firstMostMeaningfulDeployment = useMemo(
		() =>
			deploymentGroups.length > 0
				? getMostMeaningfulDeployment(deploymentGroups[deploymentGroups.length - 1].deployments)
				: undefined,
		[deploymentGroups],
	);

	const lastMostMeaningfulDeployment = useMemo(
		() =>
			deploymentGroups.length > 0
				? getMostMeaningfulDeployment(deploymentGroups[0].deployments)
				: undefined,
		[deploymentGroups],
	);

	return (
		<div css={navigateIndicatorsWrapperStyles}>
			<NavigateIndicator
				show={direction === Directions.LEFT}
				direction={Directions.LEFT}
				deployment={lastMostMeaningfulDeployment}
				onClick={() => {
					const { scrollLeft, scrollWidth } = requestViewportScrollBounding();
					const past = getPastIndicatorInfo(
						getPositionFromDate(singleDeployments[0].lastUpdated).right,
						scrollLeft,
						scrollWidth,
					);
					if (past !== null) {
						timelineScroller.scrollToPercentages({
							left: past.scrollTo,
							offset: gridSize * -5, // this leaves a single grid space as padding
							behavior: 'smooth',
						});
					}
				}}
			/>
			<NavigateIndicator
				show={direction === Directions.RIGHT}
				direction={Directions.RIGHT}
				deployment={firstMostMeaningfulDeployment}
				onClick={() => {
					const { scrollRight, scrollWidth } = requestViewportScrollBounding();
					const future = getFutureIndicatorInfo(
						getPositionFromDate(singleDeployments[deploymentGroups.length - 1].lastUpdated).left,
						scrollRight,
						scrollWidth,
					);
					if (future !== null) {
						timelineScroller.scrollToPercentages({
							left: future.scrollTo,
							offset: gridSize * -0.5, // this leaves a single grid space as padding
							behavior: 'smooth',
						});
					}
				}}
			/>
		</div>
	);
};

const isEqual = ({ deployments: prevDeps }: Props, { deployments: nextDeps }: Props): boolean =>
	areDeploymentsEqual(prevDeps, nextDeps);

const navigateIndicatorsWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${DEFAULT_ITEM_HEIGHT}px`,
	padding: `0 ${token('space.050', '4px')}`,
});

export default memo<Props>(NavigateIndicators, isEqual);
